/**
 * Find's amenity chips (Stage 6, Sam 2026-10-26): the Maps page's chips on the radar. A chip is a show filter - on, its
 * amenities are on the radar; off, they are gone - and several can be on at once (water + food + restrooms).
 *
 * Each chip shows at most AMENITY_MAX of its kind, the nearest, and only those within AMENITY_REACH_M of you: the radar is
 * for what is close, and a festival has dozens of water stations. 400 m is about a five-minute walk across a crowd,
 * roughly the distance across a big stage field; past that the map is the better tool. When nothing of the kind is that
 * close, the single nearest is still shown, so a chip never turns on to nothing.
 */
import { CHIP_GROUPS, KINDS } from './kinds';
import { CHIP_KINDS } from './festivalMapGeoJson';
import { isPublicEntrance } from './featureName';
import { haversineMeters, bearingDegrees } from '../proximity/engine/geodesy';

export const AMENITY_REACH_M = 400;
export const AMENITY_MAX = 3;

// A grouped kind rides on its head's chip, never a chip of its own; Entrance leads the row (the Maps page's order).
const GROUPED = new Set(
  Object.entries(CHIP_GROUPS).flatMap(([head, members]) => members.filter((m) => m !== head)),
);
export const CHIP_ORDER = [
  'entrance',
  ...CHIP_KINDS.filter((k) => KINDS[k].family !== 'wayfinding' && k !== 'other' && !GROUPED.has(k)),
];

/** Whether a map feature answers to a chip: Entrance is the public gates only; every other chip is its kind and group. */
export function matchesChip(kind, f) {
  if (kind === 'entrance') return isPublicEntrance(f);
  return (CHIP_GROUPS[kind] ?? [kind]).includes(f.kind);
}

/** The chips this map can fill, in the Maps page's order. */
export function chipKindsFor(model) {
  const features = model?.features ?? [];
  return CHIP_ORDER.filter((k) => features.some((f) => matchesChip(k, f)));
}

/** The amenities one chip puts on the radar: nearest first, within reach, at most AMENITY_MAX (at least the nearest). */
export function amenitiesForChip(model, kind, self) {
  if (!model || self?.lat == null || self?.lng == null) return [];
  const all = model.features
    .filter((f) => f.anchorLat != null && matchesChip(kind, f))
    .map((feature) => ({
      feature,
      kind,
      distanceMeters: haversineMeters(self.lat, self.lng, feature.anchorLat, feature.anchorLng),
      bearingDeg: bearingDegrees(self.lat, self.lng, feature.anchorLat, feature.anchorLng),
    }))
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
  const near = all.filter((a) => a.distanceMeters <= AMENITY_REACH_M).slice(0, AMENITY_MAX);
  return near.length ? near : all.slice(0, 1);
}
